// hooks/signalr/signalREventHandlers.js
export const createEventHandlers = ({
  setScheduleMetadata,
  setRecentActivity,
  setTypingUsers,
  setEditLocks,
  setMyLocks,
  refreshScheduleAsync,
  refreshScheduleAllUsersAsync,
  documentSavedCallbackRef,
  scheduleMetadataRef,
  stationRef,
  impersonatedUserId
}) => {
  // Håller reda på pågående auto-clear-timeout per fieldId (connection-scoped), så att en
  // ny "isTyping"-händelse för samma fält förnyar timeouten istället för att en äldre,
  // redan schemalagd timeout hinner radera en nyare/fräschare typing-indikator.
  const typingClearTimeouts = {};

  return {
    // ========== SCHEDULE EVENTS ==========
    onReceiveSchedule: (htmlDokumentDTO, updateTime, currentDate) => {
      setScheduleMetadata({
        html: htmlDokumentDTO.html,
        isEdited: htmlDokumentDTO.isEdited,
        conflicts: htmlDokumentDTO.conflicts,
        lastUpdated: new Date(updateTime),
        currentDate: currentDate
      });
    },

    onScheduleUpdated: (data) => {
      setScheduleMetadata(prev => ({
        ...prev,
        html: data.schedule.html,
        conflicts: data.schedule.conflicts,
        version: data.version,
        lastUpdatedBy: data.updatedBy,
        lastUpdated: data.updatedAt
      }));
    },

    onScheduleUpdatedByOther: (htmlDokumentDTO, updateTime, currentDate) => {
      console.log('🔄 Schedule updated by another user');
      setScheduleMetadata(prev => ({
        ...prev,
        html: htmlDokumentDTO.html,
        isEdited: htmlDokumentDTO.isEdited,
        conflicts: htmlDokumentDTO.conflicts,
        lastUpdated: new Date(updateTime),
        currentDate: currentDate,
        externalUpdate: true
      }));
    },

    // ========== DOCUMENT EVENTS ==========
    onDocumentSaved: (data) => {
      const station = data.Station || data.station;
      const date = data.Date || data.date;
      const savedBy = data.SavedBy || data.savedBy;
      const savedAt = data.SavedAt || data.savedAt || new Date();

      console.log('📄 DocumentSaved:', { station, date, savedBy, savedAt });

      if (!savedBy || !date) {
        console.error('❌ DocumentSaved saknar data:', data);
        return;
      }

      if (documentSavedCallbackRef.current) {
        documentSavedCallbackRef.current({
          Station: station,
          Date: date,
          SavedBy: savedBy,
          SavedAt: savedAt
        });
      }

      if (station === stationRef.current && date === scheduleMetadataRef.current?.currentDate) {
        setScheduleMetadata(prev => ({
          ...prev,
          externalUpdate: true,
          lastUpdatedBy: savedBy,
          lastUpdatedAt: savedAt
        }));
      }
    },

    onDocumentRegenerated: (data) => {
      console.log('🔄 DocumentRegenerated:', data);

      const station = data.Station || data.station;
      const date = data.Date || data.date;
      const regeneratedBy = data.RegeneratedBy || data.regeneratedBy;

      if (!regeneratedBy || !date) {
        console.warn('DocumentRegenerated saknar data');
        return;
      }

      if (documentSavedCallbackRef.current) {
        documentSavedCallbackRef.current({
          Station: station,
          Date: date,
          SavedBy: regeneratedBy,
          RegeneratedBy: regeneratedBy,
          SavedAt: data.RegeneratedAt,
          IsRegenerate: true
        });
      }

      if (station === stationRef.current && date === scheduleMetadataRef.current?.currentDate) {
        console.log('🔄 Auto-refreshing after regenerate...');
        setTimeout(() => {
          refreshScheduleAsync(date, impersonatedUserId);
        }, 500);
      }
    },

    // ========== USER ACTIVITY EVENTS ==========
    onUserActivity: (activity) => {
      console.log('👥 User activity:', activity);
      setRecentActivity(prev => [...prev, activity].slice(-10));
      if (activity.action === 'schedule_updated') {
        console.log(`${activity.userId} uppdaterade ${activity.station}`);
      }
    },


    // ========== TYPING EVENTS ==========
    onUserTyping: (data) => {
      setTypingUsers(prev => ({
        ...prev,
        [data.fieldId]: data.isTyping ? {
          userId: data.userId,
          userName: data.userName,
          fieldId: data.fieldId
        } : undefined
      }));

      if (typingClearTimeouts[data.fieldId]) {
        clearTimeout(typingClearTimeouts[data.fieldId]);
        delete typingClearTimeouts[data.fieldId];
      }

      if (data.isTyping) {
        typingClearTimeouts[data.fieldId] = setTimeout(() => {
          delete typingClearTimeouts[data.fieldId];
          setTypingUsers(prev => {
            const updated = { ...prev };
            if (updated[data.fieldId]?.userId === data.userId) {
              delete updated[data.fieldId];
            }
            return updated;
          });
        }, 10000);
      }
    },

    // ========== LOCK EVENTS ==========
    onEntityLocked: (data) => {
      setEditLocks(prev => ({
        ...prev,
        [data.entityId]: {
          lockedBy: data.lockedBy,
          expiresAt: data.expiresAt
        }
      }));
    },

    onEntityUnlocked: (data) => {
      setEditLocks(prev => {
        const updated = { ...prev };
        delete updated[data.entityId];
        return updated;
      });
    },

    onLockExpired: (data) => {
      setMyLocks(prev => {
        const updated = { ...prev };
        delete updated[data.entityId];
        return updated;
      });
    },

    // ========== HEARTBEAT EVENTS ==========
    onPong: () => {
    },

    // ========== LEGACY EVENTS (för bakåtkompatibilitet) ==========
    onRequestSchedule: (currentDate, userId) => {
      refreshScheduleAsync(currentDate, userId);
    },

    onRequestScheduleAllUsers: (currentDate) => {
      refreshScheduleAllUsersAsync(currentDate, "");
    },

    onPollSchedule: (date) => {
      console.log('🔄 PollSchedule:', date);
      refreshScheduleAllUsersAsync(date, "");
    },

    onSwitchToDate: (newDate) => {
      console.log('🌙 SwitchToDate:', newDate);
      refreshScheduleAllUsersAsync(newDate, "");
    },

  };
};

// Hjälpfunktion för att registrera alla handlers på en connection
export const registerEventHandlers = (connection, handlers) => {
  connection.on('ReceiveSchedule', handlers.onReceiveSchedule);
  connection.on('ScheduleUpdated', handlers.onScheduleUpdated);
  connection.on('ScheduleUpdatedByOther', handlers.onScheduleUpdatedByOther);
  connection.on('DocumentSaved', handlers.onDocumentSaved);
  connection.on('DocumentRegenerated', handlers.onDocumentRegenerated);
  connection.on('UserActivity', handlers.onUserActivity);
  connection.on('UserTyping', handlers.onUserTyping);
  connection.on('EntityLocked', handlers.onEntityLocked);
  connection.on('EntityUnlocked', handlers.onEntityUnlocked);
  connection.on('LockExpired', handlers.onLockExpired);
  connection.on('Pong', handlers.onPong);
  connection.on('RequestSchedule', handlers.onRequestSchedule);
  connection.on('RequestScheduleAllUsers', handlers.onRequestScheduleAllUsers);
  connection.on('PollSchedule', handlers.onPollSchedule);        
  connection.on('SwitchToDate', handlers.onSwitchToDate);        
};